import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatAuthError } from "@/lib/api";
import { AgiLogo } from "@/components/AgiLogo";
import { COMPANY, OTP_EMAIL } from "@/lib/company";
import { WindowDiagram } from "@/components/WindowDiagram";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, ArrowRight, Mail, KeyRound, RefreshCw } from "lucide-react";

export default function Login() {
  const { user, checked, login, requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("otp");
  const [pwEmail, setPwEmail] = useState(OTP_EMAIL);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (checked && user) navigate("/");
  }, [checked, user, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(pwEmail, password);
      navigate("/");
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const d = await requestOtp(OTP_EMAIL);
      setOtpSent(true);
      if (d.demo && d.code) setCode(d.code);
      setInfo(d.message || "A code has been sent to your email.");
      setCooldown(45);
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyOtp(OTP_EMAIL, code);
      navigate("/");
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      {/* Left: form */}
      <div className="w-full lg:w-[42%] flex items-center justify-center p-8 md:p-14">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="flex items-center gap-3 mb-10">
            <AgiLogo size={44} />
            <div>
              <div className="font-display font-extrabold tracking-tight text-slate-900 leading-none text-lg">
                {COMPANY.name}
              </div>
              <div className="text-[10px] tracking-[0.35em] text-slate-400 mt-1">
                {COMPANY.tagline}
              </div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
            Sign in
          </h1>
          <p className="text-sm text-slate-500 mt-2 mb-6">
            Quotation studio for {COMPANY.system}.
          </p>

          <Tabs value={mode} onValueChange={(v) => { setMode(v); setError(""); setInfo(""); }} className="w-full">
            <TabsList className="rounded-sm bg-slate-100 w-full grid grid-cols-2 mb-6">
              <TabsTrigger value="otp" className="rounded-sm" data-testid="tab-otp">
                <Mail className="w-4 h-4 mr-1.5" /> Email OTP
              </TabsTrigger>
              <TabsTrigger value="password" className="rounded-sm" data-testid="tab-password">
                <KeyRound className="w-4 h-4 mr-1.5" /> Password
              </TabsTrigger>
            </TabsList>

            <TabsContent value="otp">
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Company Email</Label>
                  <Input value={OTP_EMAIL} readOnly disabled data-testid="otp-email-input" className="rounded-sm h-11 bg-slate-50" />
                </div>
                {otpSent && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-slate-500">6-Digit Code</Label>
                    <Input
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="------"
                      data-testid="otp-code-input"
                      className="rounded-sm h-11 font-mono text-lg tracking-[0.5em] text-center"
                    />
                  </div>
                )}
                {info && (
                  <div data-testid="otp-info" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-sm">
                    {info}
                  </div>
                )}
                {error && (
                  <div data-testid="otp-error" className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-sm">
                    {error}
                  </div>
                )}
                {!otpSent ? (
                  <Button type="button" onClick={sendOtp} disabled={loading} data-testid="otp-send-button" className="w-full h-11 rounded-sm bg-blue-600 hover:bg-blue-700 text-white">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center gap-2">Send me a code <ArrowRight className="w-4 h-4" /></span>}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <Button type="button" onClick={verify} disabled={loading || code.length < 6} data-testid="otp-verify-button" className="w-full h-11 rounded-sm bg-blue-600 hover:bg-blue-700 text-white">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center gap-2">Verify & Sign in <ArrowRight className="w-4 h-4" /></span>}
                    </Button>
                    <button type="button" onClick={sendOtp} disabled={cooldown > 0} data-testid="otp-resend-button" className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1.5 mx-auto disabled:opacity-50">
                      <RefreshCw className="w-3 h-3" /> {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                    </button>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="password">
              <form onSubmit={submitPassword} className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
                  <Input id="email" type="email" data-testid="login-email-input" value={pwEmail} onChange={(e) => setPwEmail(e.target.value)} className="rounded-sm h-11" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">Password</Label>
                  <Input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm h-11" required />
                </div>
                {error && (
                  <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-sm">
                    {error}
                  </div>
                )}
                <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full h-11 rounded-sm bg-blue-600 hover:bg-blue-700 text-white font-medium group">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center gap-2">Enter Studio <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-xs text-slate-400 mt-10">
            {COMPANY.address_1}
          </p>
        </motion.div>
      </div>

      {/* Right: technical hero */}
      <div className="hidden lg:flex lg:w-[58%] bg-slate-900 blueprint-grid relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-60" />
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="relative z-10 grid grid-cols-3 gap-6 p-12 w-full max-w-2xl"
        >
          {[
            { t: "2 Track Sliding", w: 1800, h: 1810 },
            { t: "Casement", w: 900, h: 1200 },
            { t: "Fixed", w: 1820, h: 1820 },
            { t: "Ventilator Louver Type", w: 590, h: 580 },
            { t: "3 Track Sliding", w: 2180, h: 2080 },
            { t: "Top Hung", w: 1200, h: 700 },
          ].map((d, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.07 }}
              className="bg-white/95 border border-white/20 rounded-sm p-2 h-32 flex flex-col"
            >
              <WindowDiagram typology={d.t} width={d.w} height={d.h} className="flex-1 w-full" showDims={false} />
              <div className="text-[9px] text-slate-500 font-mono text-center truncate">{d.t}</div>
            </motion.div>
          ))}
        </motion.div>
        <div className="absolute bottom-8 left-12 z-10 text-white/70">
          <div className="text-xs tracking-[0.3em] uppercase">AI Elevation Engine</div>
          <div className="text-sm mt-1 text-white/50">Typology + dimensions → instant technical drawing</div>
        </div>
      </div>
    </div>
  );
}
